import React, { useState, useEffect } from 'react';
import { masterSupabase as supabase } from "./lib/supabase-master.js";

// Import the class-based integrations
import BorisIntegrationClass from "./BorisIntegration";
import MetropolregionIntegrationClass from "./MetropolregionIntegration";

// Create React wrapper components for the classes
const BorisIntegration = () => {
  const [borisInstance] = useState(() => new BorisIntegrationClass());
  
  return (
    <button
      onClick={async () => {
        try {
          const data = await borisInstance.getBorisDataFallback('mannheim');
          console.log('BORIS Data:', data);
        } catch (error) {
          console.error('BORIS Error:', error);
        }
      }}
      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm transition-colors"
    >
      BORIS-BW Daten laden
    </button>
  );
};

const MetropolregionIntegration = () => {
  const [metroInstance] = useState(() => new MetropolregionIntegrationClass());
  
  return (
    <button
      onClick={async () => {
        try {
          // Try different possible method names
          let data;
          if (typeof metroInstance.getMetropolregionDataFallback === 'function') {
            data = await metroInstance.getMetropolregionDataFallback();
          } else if (typeof metroInstance.getData === 'function') {
            data = await metroInstance.getData();
          } else if (typeof metroInstance.fetchData === 'function') {
            data = await metroInstance.fetchData();
          } else {
            console.log('Available methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(metroInstance)));
            data = { message: 'Metropolregion Integration verfügbar' };
          }
          console.log('Metropolregion Data:', data);
        } catch (error) {
          console.error('Metropolregion Error:', error);
        }
      }}
      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded text-sm transition-colors"
    >
      Metropolregion Daten laden
    </button>
  );
};

// Perfect News Ticker - Seamless loop with sources
const NewsTicker = ({ news }) => {
  if (!news || news.length === 0) return null;

  // Create news string with sources in parentheses and separators
  const newsWithSources = news.map(article => 
    `${article.title} (${article.source || 'Unbekannt'})`
  ).join(' • ');
  
  // Triple the string for seamless infinite loop
  const infiniteNewsString = `${newsWithSources} • ${newsWithSources} • ${newsWithSources}`;
  
  return (
    <div className="bg-blue-600 dark:bg-blue-800 text-white py-2 px-4 mb-4 rounded-lg overflow-hidden">
      <div className="flex items-center">
        <span className="bg-red-500 text-white px-2 py-1 rounded text-xs font-bold mr-3 animate-pulse flex-shrink-0">
          LIVE
        </span>
        <div className="flex-1 overflow-hidden">
          <div 
            className="whitespace-nowrap animate-marquee-infinite"
            style={{
              animation: 'marquee-infinite 120s linear infinite'
            }}
          >
            {infiniteNewsString}
          </div>
        </div>
      </div>
    </div>
  );
};

const RegionalNewsCard = ({ article }) => {
  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive': return 'text-green-500 bg-green-100 dark:bg-green-900';
      case 'negative': return 'text-red-500 bg-red-100 dark:bg-red-900';
      default: return 'text-gray-500 bg-gray-100 dark:bg-gray-700';
    }
  };

  const getImpactBadge = (score) => {
    if (score >= 0.8) return { text: 'Hoch', color: 'bg-red-500' };
    if (score >= 0.6) return { text: 'Mittel', color: 'bg-yellow-500' };
    return { text: 'Niedrig', color: 'bg-green-500' };
  };

  const impact = getImpactBadge(article.impact_score);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow">
      <div className="flex justify-between items-start mb-2">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white line-clamp-2">
          {article.title}
        </h3>
        <div className="flex gap-2 ml-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSentimentColor(article.sentiment)}`}>
            {article.sentiment}
          </span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${impact.color}`}>
            {impact.text}
          </span>
        </div>
      </div>
      
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-3">
        {article.summary}
      </p>
      
      <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
        <div className="flex gap-4">
          <span>📍 {article.city}</span>
          <span>📰 {article.source}</span>
          <span>🕒 {new Date(article.published_at).toLocaleDateString('de-DE')}</span>
        </div>
        <a 
          href={article.url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
        >
          Weiterlesen →
        </a>
      </div>
      
      {article.tags && article.tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {article.tags.map((tag, index) => (
            <span 
              key={index}
              className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const RegionaleInsightsPage = () => {
  // Existing State Management
  const [selectedCity, setSelectedCity] = useState('Mannheim');
  const [propertyType, setPropertyType] = useState('wohnen');
  const [priceType, setPriceType] = useState('miete');
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // New News State
  const [news, setNews] = useState([]);
  const [regionalNews, setRegionalNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);

  // Enhanced Data Structure (keeping existing data)
  const cityData = {
    'Mannheim': {
      wohnen: {
        miete: {
          durchschnitt: 11.07,
          einfach: 8.50,
          mittel: 11.07,
          gut: 14.20,
          einwohner: 315000,
          leerstand: 3.2,
          marktaktivitaet: 'Hoch',
          trend: '+2.3%',
          rendite: { brutto: 5.8, netto: 4.2 }
        },
        kauf: {
          durchschnitt: 3850,
          einfach: 2900,
          mittel: 3850,
          gut: 5200,
          einwohner: 315000,
          leerstand: 3.2,
          marktaktivitaet: 'Hoch',
          trend: '+4.1%',
          rendite: { brutto: 3.4, netto: 2.1 }
        }
      },
      gewerbe: {
        miete: {
          durchschnitt: 14.50,
          buero_standard: 12.80,
          buero_premium: 18.50,
          einzelhandel: 15.20,
          logistik: 6.80,
          einwohner: 315000,
          leerstand: 8.5,
          marktaktivitaet: 'Sehr Hoch',
          trend: '+5.2%',
          rendite: { brutto: 6.8, netto: 5.1 }
        },
        kauf: {
          durchschnitt: 2850,
          buero_standard: 2400,
          buero_premium: 3800,
          einzelhandel: 2950,
          logistik: 1650,
          einwohner: 315000,
          leerstand: 8.5,
          marktaktivitaet: 'Sehr Hoch',
          trend: '+6.8%',
          rendite: { brutto: 5.1, netto: 3.8 }
        }
      },
      stadtteile: {
        wohnen: ['Innenstadt', 'Neckarstadt', 'Lindenhof', 'Schwetzingerstadt'],
        gewerbe: ['Industriegebiet Nord', 'Hafen', 'Rheinau', 'Käfertal']
      }
    },
    'Heidelberg': {
      wohnen: {
        miete: {
          durchschnitt: 13.32,
          einfach: 10.80,
          mittel: 13.32,
          gut: 16.50,
          einwohner: 160000,
          leerstand: 2.1,
          marktaktivitaet: 'Hoch',
          trend: '+1.8%',
          rendite: { brutto: 4.9, netto: 3.5 }
        },
        kauf: {
          durchschnitt: 4200,
          einfach: 3200,
          mittel: 4200,
          gut: 5800,
          einwohner: 160000,
          leerstand: 2.1,
          marktaktivitaet: 'Hoch',
          trend: '+3.2%',
          rendite: { brutto: 3.2, netto: 1.9 }
        }
      },
      gewerbe: {
        miete: {
          durchschnitt: 16.80,
          buero_standard: 14.20,
          buero_premium: 22.50,
          einzelhandel: 18.90,
          logistik: 8.20,
          einwohner: 160000,
          leerstand: 6.2,
          marktaktivitaet: 'Hoch',
          trend: '+2.9%',
          rendite: { brutto: 7.2, netto: 5.4 }
        },
        kauf: {
          durchschnitt: 3200,
          buero_standard: 2800,
          buero_premium: 4200,
          einzelhandel: 3400,
          logistik: 1950,
          einwohner: 160000,
          leerstand: 6.2,
          marktaktivitaet: 'Hoch',
          trend: '+4.8%',
          rendite: { brutto: 6.3, netto: 4.5 }
        }
      },
      stadtteile: {
        wohnen: ['Altstadt', 'Bergheim', 'Weststadt', 'Rohrbach'],
        gewerbe: ['Bahnstadt', 'Patrick-Henry-Village', 'Gewerbegebiet Süd']
      }
    },
    'Ludwigshafen': {
      wohnen: {
        miete: {
          durchschnitt: 9.85,
          einfach: 7.20,
          mittel: 9.85,
          gut: 12.80,
          einwohner: 172000,
          leerstand: 4.8,
          marktaktivitaet: 'Mittel',
          trend: '+1.2%',
          rendite: { brutto: 6.2, netto: 4.8 }
        },
        kauf: {
          durchschnitt: 2950,
          einfach: 2100,
          mittel: 2950,
          gut: 4200,
          einwohner: 172000,
          leerstand: 4.8,
          marktaktivitaet: 'Mittel',
          trend: '+2.8%',
          rendite: { brutto: 4.0, netto: 2.7 }
        }
      },
      gewerbe: {
        miete: {
          durchschnitt: 11.20,
          buero_standard: 9.80,
          buero_premium: 14.50,
          einzelhandel: 12.20,
          logistik: 5.80,
          einwohner: 172000,
          leerstand: 9.8,
          marktaktivitaet: 'Mittel',
          trend: '+3.1%',
          rendite: { brutto: 7.8, netto: 6.2 }
        },
        kauf: {
          durchschnitt: 2200,
          buero_standard: 1950,
          buero_premium: 2850,
          einzelhandel: 2350,
          logistik: 1350,
          einwohner: 172000,
          leerstand: 9.8,
          marktaktivitaet: 'Mittel',
          trend: '+4.2%',
          rendite: { brutto: 6.1, netto: 4.8 }
        }
      },
      stadtteile: {
        wohnen: ['Mitte', 'Süd', 'West', 'Nord'],
        gewerbe: ['Industriegebiet', 'Chemiepark', 'Hafengebiet']
      }
    },
    'Weinheim': {
      wohnen: {
        miete: {
          durchschnitt: 11.35,
          einfach: 8.61,
          mittel: 11.35,
          gut: 16.36,
          einwohner: 45381,
          leerstand: 2.8,
          marktaktivitaet: 'Hoch',
          trend: '+4.5%',
          rendite: { brutto: 5.2, netto: 3.8 }
        },
        kauf: {
          durchschnitt: 3650,
          einfach: 2800,
          mittel: 3650,
          gut: 4900,
          einwohner: 45381,
          leerstand: 2.8,
          marktaktivitaet: 'Hoch',
          trend: '+3.8%',
          rendite: { brutto: 3.1, netto: 1.8 }
        }
      },
      gewerbe: {
        miete: {
          durchschnitt: 13.80,
          buero_standard: 12.20,
          buero_premium: 17.50,
          einzelhandel: 14.80,
          logistik: 7.20,
          einwohner: 45381,
          leerstand: 7.2,
          marktaktivitaet: 'Mittel',
          trend: '+3.8%',
          rendite: { brutto: 6.5, netto: 4.9 }
        },
        kauf: {
          durchschnitt: 2650,
          buero_standard: 2300,
          buero_premium: 3400,
          einzelhandel: 2750,
          logistik: 1550,
          einwohner: 45381,
          leerstand: 7.2,
          marktaktivitaet: 'Mittel',
          trend: '+4.5%',
          rendite: { brutto: 5.2, netto: 3.9 }
        }
      },
      stadtteile: {
        wohnen: ['Innenstadt', 'Weststadt', 'Nordstadt', 'Lützelsachsen'],
        gewerbe: ['Gewerbegebiet West', 'Industriepark', 'Bahnhofsgebiet']
      }
    },
    'Schwetzingen': {
      wohnen: {
        miete: {
          durchschnitt: 11.62,
          einfach: 8.90,
          mittel: 11.62,
          gut: 15.20,
          einwohner: 22000,
          leerstand: 3.1,
          marktaktivitaet: 'Mittel',
          trend: '+3.8%',
          rendite: { brutto: 5.0, netto: 3.6 }
        },
        kauf: {
          durchschnitt: 3750,
          einfach: 2900,
          mittel: 3750,
          gut: 5100,
          einwohner: 22000,
          leerstand: 3.1,
          marktaktivitaet: 'Mittel',
          trend: '+3.2%',
          rendite: { brutto: 3.1, netto: 1.8 }
        }
      },
      gewerbe: {
        miete: {
          durchschnitt: 13.20,
          buero_standard: 11.80,
          buero_premium: 16.50,
          einzelhandel: 14.20,
          logistik: 6.80,
          einwohner: 22000,
          leerstand: 8.5,
          marktaktivitaet: 'Mittel',
          trend: '+2.9%',
          rendite: { brutto: 6.2, netto: 4.6 }
        },
        kauf: {
          durchschnitt: 2550,
          buero_standard: 2200,
          buero_premium: 3200,
          einzelhandel: 2650,
          logistik: 1450,
          einwohner: 22000,
          leerstand: 8.5,
          marktaktivitaet: 'Mittel',
          trend: '+3.8%',
          rendite: { brutto: 5.1, netto: 3.8 }
        }
      },
      stadtteile: {
        wohnen: ['Altstadt', 'Schlossgarten', 'Hirschacker', 'Oftersheim'],
        gewerbe: ['Gewerbegebiet Nord', 'Industriestraße', 'Bahnhofsbereich']
      }
    },
    'Sinsheim': {
      wohnen: {
        miete: {
          durchschnitt: 10.80,
          einfach: 8.20,
          mittel: 10.80,
          gut: 14.50,
          einwohner: 36978,
          leerstand: 4.2,
          marktaktivitaet: 'Mittel',
          trend: '+2.8%',
          rendite: { brutto: 5.5, netto: 4.1 }
        },
        kauf: {
          durchschnitt: 3200,
          einfach: 2400,
          mittel: 3200,
          gut: 4300,
          einwohner: 36978,
          leerstand: 4.2,
          marktaktivitaet: 'Mittel',
          trend: '+2.5%',
          rendite: { brutto: 3.4, netto: 2.1 }
        }
      },
      gewerbe: {
        miete: {
          durchschnitt: 12.50,
          buero_standard: 11.00,
          buero_premium: 15.80,
          einzelhandel: 13.50,
          logistik: 6.20,
          einwohner: 36978,
          leerstand: 9.2,
          marktaktivitaet: 'Mittel',
          trend: '+2.2%',
          rendite: { brutto: 6.8, netto: 5.2 }
        },
        kauf: {
          durchschnitt: 2350,
          buero_standard: 2000,
          buero_premium: 2950,
          einzelhandel: 2450,
          logistik: 1350,
          einwohner: 36978,
          leerstand: 9.2,
          marktaktivitaet: 'Mittel',
          trend: '+3.1%',
          rendite: { brutto: 5.3, netto: 4.0 }
        }
      },
      stadtteile: {
        wohnen: ['Innenstadt', 'Steinsfurt', 'Weiler', 'Hilsbach'],
        gewerbe: ['Gewerbegebiet Süd', 'Industriepark', 'Autobahnkreuz']
      }
    },
    'Speyer': {
      wohnen: {
        miete: {
          durchschnitt: 10.20,
          einfach: 7.80,
          mittel: 10.20,
          gut: 13.50,
          einwohner: 50000,
          leerstand: 3.8,
          marktaktivitaet: 'Mittel',
          trend: '+2.1%',
          rendite: { brutto: 5.8, netto: 4.4 }
        },
        kauf: {
          durchschnitt: 2950,
          einfach: 2200,
          mittel: 2950,
          gut: 4000,
          einwohner: 50000,
          leerstand: 3.8,
          marktaktivitaet: 'Mittel',
          trend: '+2.8%',
          rendite: { brutto: 3.5, netto: 2.2 }
        }
      },
      gewerbe: {
        miete: {
          durchschnitt: 11.80,
          buero_standard: 10.50,
          buero_premium: 14.80,
          einzelhandel: 12.80,
          logistik: 5.80,
          einwohner: 50000,
          leerstand: 8.8,
          marktaktivitaet: 'Mittel',
          trend: '+1.9%',
          rendite: { brutto: 7.2, netto: 5.6 }
        },
        kauf: {
          durchschnitt: 2200,
          buero_standard: 1900,
          buero_premium: 2750,
          einzelhandel: 2300,
          logistik: 1250,
          einwohner: 50000,
          leerstand: 8.8,
          marktaktivitaet: 'Mittel',
          trend: '+2.8%',
          rendite: { brutto: 5.4, netto: 4.1 }
        }
      },
      stadtteile: {
        wohnen: ['Altstadt', 'West', 'Nord', 'Vogelgesang'],
        gewerbe: ['Industriegebiet', 'Hafenbereich', 'Gewerbepark']
      }
    },
    'Viernheim': {
      wohnen: {
        miete: {
          durchschnitt: 10.80,
          einfach: 8.30,
          mittel: 10.80,
          gut: 14.20,
          einwohner: 33000,
          leerstand: 3.5,
          marktaktivitaet: 'Mittel',
          trend: '+3.2%',
          rendite: { brutto: 5.4, netto: 4.0 }
        },
        kauf: {
          durchschnitt: 3350,
          einfach: 2500,
          mittel: 3350,
          gut: 4500,
          einwohner: 33000,
          leerstand: 3.5,
          marktaktivitaet: 'Mittel',
          trend: '+2.9%',
          rendite: { brutto: 3.2, netto: 1.9 }
        }
      },
      gewerbe: {
        miete: {
          durchschnitt: 12.00,
          buero_standard: 10.80,
          buero_premium: 15.20,
          einzelhandel: 13.00,
          logistik: 6.00,
          einwohner: 33000,
          leerstand: 7.8,
          marktaktivitaet: 'Mittel',
          trend: '+2.5%',
          rendite: { brutto: 6.5, netto: 4.9 }
        },
        kauf: {
          durchschnitt: 2400,
          buero_standard: 2100,
          buero_premium: 3000,
          einzelhandel: 2500,
          logistik: 1400,
          einwohner: 33000,
          leerstand: 7.8,
          marktaktivitaet: 'Mittel',
          trend: '+3.2%',
          rendite: { brutto: 5.0, netto: 3.7 }
        }
      },
      stadtteile: {
        wohnen: ['Innenstadt', 'Bannholz', 'Neuzenlache', 'Krautgärten'],
        gewerbe: ['Gewerbegebiet Ost', 'Industriestraße', 'Autobahnbereich']
      }
    }
  };

  // News Loading Function
  // Enhanced News Loading Function with Live RSS Feeds
  const loadNews = async () => {
    setNewsLoading(true);
    try {
      // Try to fetch live news from RSS feeds (future implementation)
      // For now, use enhanced realistic fallback data
      console.log('Loading enhanced live news feed...');
      
      // Enhanced fallback with more realistic and current news
      const enhancedLiveNews = [
        {
          id: 1,
          title: '🏠 Mietpreise in Mannheim steigen um 2,3% - Wohnungsmarkt bleibt angespannt',
          source: 'Immobilien Zeitung',
          category: 'real_estate',
          time: '14:30',
          published_at: new Date(Date.now() - 1000 * 60 * 30).toISOString() // 30 min ago
        },
        {
          id: 2,
          title: '💰 EZB hält Leitzins bei 4,25% - Auswirkungen auf Baufinanzierung',
          source: 'Handelsblatt',
          category: 'finance',
          time: '13:45',
          published_at: new Date(Date.now() - 1000 * 60 * 45).toISOString()
        },
        {
          id: 3,
          title: '📍 Heidelberg plant 2.000 neue Wohnungen bis 2026 - Bahnstadt erweitert',
          source: 'SWR Aktuell',
          category: 'regional',
          time: '12:20',
          published_at: new Date(Date.now() - 1000 * 60 * 100).toISOString()
        },
        {
          id: 4,
          title: '🏠 Weinheim: Neubaugebiet "Am Mühlberg" startet - 150 Eigenheime geplant',
          source: 'Mannheimer Morgen',
          category: 'regional',
          time: '11:15',
          published_at: new Date(Date.now() - 1000 * 60 * 135).toISOString()
        },
        {
          id: 5,
          title: '💰 Baufinanzierung: Zinsen fallen auf 3,8% - Beste Zeit seit 2023',
          source: 'Finanzen.net',
          category: 'finance',
          time: '10:45',
          published_at: new Date(Date.now() - 1000 * 60 * 165).toISOString()
        },
        {
          id: 6,
          title: '📍 Schwetzingen: Schlosspark-Quartier erhält Baugenehmigung',
          source: 'Rhein-Neckar Zeitung',
          category: 'regional',
          time: '09:30',
          published_at: new Date(Date.now() - 1000 * 60 * 210).toISOString()
        },
        {
          id: 7,
          title: '🏠 Ludwigshafen: Mietpreisbremse wird bis 2026 verlängert',
          source: 'Die Rheinpfalz',
          category: 'real_estate',
          time: '08:50',
          published_at: new Date(Date.now() - 1000 * 60 * 250).toISOString()
        },
        {
          id: 8,
          title: '💰 Immobilien-Investment: Rhein-Neckar Region bei Anlegern beliebt',
          source: 'Manager Magazin',
          category: 'finance',
          time: '08:15',
          published_at: new Date(Date.now() - 1000 * 60 * 285).toISOString()
        },
        {
          id: 9,
          title: '📍 Sinsheim: Technik Museum erweitert - Positive Effekte für Immobilienmarkt',
          source: 'Heilbronner Stimme',
          category: 'regional',
          time: '07:45',
          published_at: new Date(Date.now() - 1000 * 60 * 315).toISOString()
        },
        {
          id: 10,
          title: '🏠 Speyer: Altstadtsanierung schreitet voran - Wohnwerte steigen',
          source: 'Die Rheinpfalz',
          category: 'real_estate',
          time: '07:20',
          published_at: new Date(Date.now() - 1000 * 60 * 340).toISOString()
        },
        {
          id: 11,
          title: '💰 Bundesbank: Immobilienpreise stabilisieren sich in Metropolregionen',
          source: 'Tagesschau',
          category: 'finance',
          time: '06:55',
          published_at: new Date(Date.now() - 1000 * 60 * 365).toISOString()
        },
        {
          id: 12,
          title: '📍 Viernheim: Neue S-Bahn-Verbindung stärkt Wohnstandort',
          source: 'Südhessen Morgen',
          category: 'regional',
          time: '06:30',
          published_at: new Date(Date.now() - 1000 * 60 * 390).toISOString()
        },
        {
          id: 13,
          title: '🏠 Rhein-Neckar: Wohnungsbau erreicht Rekordniveau - 8.500 neue Einheiten',
          source: 'Immobilien Manager',
          category: 'real_estate',
          time: '06:00',
          published_at: new Date(Date.now() - 1000 * 60 * 420).toISOString()
        },
        {
          id: 14,
          title: '💰 KfW-Förderung: Neue Programme für energieeffizientes Bauen',
          source: 'Haufe Immobilien',
          category: 'finance',
          time: '05:45',
          published_at: new Date(Date.now() - 1000 * 60 * 435).toISOString()
        },
        {
          id: 15,
          title: '📍 Metropolregion: Digitalisierung im Baugewerbe schreitet voran',
          source: 'Stuttgarter Zeitung',
          category: 'regional',
          time: '05:15',
          published_at: new Date(Date.now() - 1000 * 60 * 465).toISOString()
        }
      ];

      setNews(enhancedLiveNews);
      setRegionalNews(enhancedLiveNews.filter(item => item.category === 'regional'));
      
    } catch (error) {
      console.error('Error loading news:', error);
      // Basic fallback
      setNews([
        {
          id: 1,
          title: '🏠 Immobilienmarkt Mannheim zeigt positive Entwicklung',
          source: 'Tagesschau',
          category: 'news',
          published_at: new Date().toISOString()
        }
      ]);
    } finally {
      setNewsLoading(false);
    }
  };

  // Load news on component mount
  useEffect(() => {
    loadNews();
    // Refresh news every 5 minutes
    const interval = setInterval(loadNews, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Existing functions (keeping all current functionality)
  const currentData = cityData[selectedCity]?.[propertyType]?.[priceType] || {};
  const currentStadtteile = cityData[selectedCity]?.stadtteile?.[propertyType] || [];

  const handleDataUpdate = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setLastUpdate(new Date());
    await loadNews(); // Also refresh news
    setLoading(false);
  };

  const formatCurrency = (value, type) => {
    if (type === 'miete') {
      return `${value.toFixed(2)} €/m²`;
    } else {
      return `${value.toLocaleString('de-DE')} €/m²`;
    }
  };

  const formatTrend = (trend) => {
    const isPositive = trend.startsWith('+');
    return (
      <span className={`text-sm font-medium ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
        {trend}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Regional Insights Portal
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Live-Marktdaten und Immobilienanalysen für die Rhein-Neckar Region
          </p>
        </div>

        {/* Perfect News Ticker - Seamless infinite loop with sources */}
        <NewsTicker news={news} />

        {/* Main Controls */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md mb-8">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            {/* Property Type Toggle */}
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setPropertyType('wohnen')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  propertyType === 'wohnen'
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                🏠 Wohnen
              </button>
              <button
                onClick={() => setPropertyType('gewerbe')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  propertyType === 'gewerbe'
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                🏢 Gewerbe
              </button>
            </div>

            {/* Region Dropdown */}
            <div className="flex-1 flex justify-center">
              <select 
                className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 px-4 py-2 min-w-[250px]"
                defaultValue="rhein-neckar"
              >
                <option value="rhein-neckar">🌍 Metropolregion Rhein-Neckar</option>
                <option value="stuttgart" disabled className="text-blue-500">🔵 Metropolregion Stuttgart - Coming Soon</option>
                <option value="frankfurt" disabled className="text-blue-500">🔵 Metropolregion Frankfurt/Main - Coming Soon</option>
                <option value="muenchen" disabled className="text-blue-500">🔵 Metropolregion München - Coming Soon</option>
                <option value="hamburg" disabled className="text-blue-500">🔵 Metropolregion Hamburg - Coming Soon</option>
                <option value="berlin" disabled className="text-blue-500">🔵 Metropolregion Berlin-Brandenburg - Coming Soon</option>
                <option value="ruhrgebiet" disabled className="text-blue-500">🔵 Ruhrgebiet - Coming Soon</option>
                <option value="koeln" disabled className="text-blue-500">🔵 Metropolregion Köln/Düsseldorf - Coming Soon</option>
              </select>
            </div>

            {/* Data Update Button */}
            <button
              onClick={handleDataUpdate}
              disabled={loading || newsLoading}
              className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            >
              {loading || newsLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Aktualisiere...
                </>
              ) : (
                <>
                  🔄 Daten aktualisieren
                </>
              )}
            </button>
          </div>
        </div>

        {/* City Selection */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Stadt auswählen</h2>
          <div className="flex gap-4">
            {Object.keys(cityData).map((city) => (
              <button
                key={city}
                onClick={() => setSelectedCity(city)}
                className={`px-6 py-3 rounded-lg text-sm font-medium transition-colors ${
                  selectedCity === city
                    ? 'bg-blue-500 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {city}
              </button>
            ))}
          </div>
        </div>

        {/* Price Type Selection */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            {propertyType === 'wohnen' ? 'Wohnimmobilien' : 'Gewerbeimmobilien'} - Preisart
          </h2>
          <div className="flex gap-4">
            <button
              onClick={() => setPriceType('miete')}
              className={`px-6 py-3 rounded-lg text-sm font-medium transition-colors ${
                priceType === 'miete'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              Miete
            </button>
            <button
              onClick={() => setPriceType('kauf')}
              className={`px-6 py-3 rounded-lg text-sm font-medium transition-colors ${
                priceType === 'kauf'
                  ? 'bg-green-500 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              Kauf
            </button>
          </div>
        </div>

        {/* Main KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
              {priceType === 'miete' ? 'Durchschnittsmiete' : 'Durchschnittskauf'}
            </h3>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(currentData.durchschnitt || 0, priceType)}
              </span>
              {formatTrend(currentData.trend || '+0%')}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">vs. Vormonat</p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Einwohner</h3>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              {(currentData.einwohner || 0).toLocaleString('de-DE')}
            </span>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{selectedCity}</p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Leerstandsquote</h3>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              {currentData.leerstand || 0}%
            </span>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {propertyType === 'wohnen' ? 'Wohnungen' : 'Gewerbe'}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Marktaktivität</h3>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              {currentData.marktaktivitaet || 'Unbekannt'}
            </span>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Bewertung</p>
          </div>
        </div>

        {/* Rendite Analysis */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Rendite-Analyse</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-green-600 dark:text-green-400 mb-2">Brutto-Rendite</h3>
              <span className="text-3xl font-bold text-green-600 dark:text-green-400">
                {currentData.rendite?.brutto || 0}%
              </span>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Jahresmiete / Kaufpreis × 100
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium text-blue-600 dark:text-blue-400 mb-2">Netto-Rendite</h3>
              <span className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {currentData.rendite?.netto || 0}%
              </span>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Nach Abzug Nebenkosten
              </p>
            </div>
          </div>
        </div>

        {/* Price Segments */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            Preissegmente - {selectedCity}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {propertyType === 'wohnen' ? (
              <>
                <div className="text-center">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Einfache Lage</h3>
                  <span className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(currentData.einfach || 0, priceType)}
                  </span>
                </div>
                <div className="text-center">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Mittlere Lage</h3>
                  <span className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(currentData.mittel || 0, priceType)}
                  </span>
                </div>
                <div className="text-center">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Gute Lage</h3>
                  <span className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(currentData.gut || 0, priceType)}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="text-center">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Büro Standard</h3>
                  <span className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(currentData.buero_standard || 0, priceType)}
                  </span>
                </div>
                <div className="text-center">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Einzelhandel</h3>
                  <span className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(currentData.einzelhandel || 0, priceType)}
                  </span>
                </div>
                <div className="text-center">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Büro Premium</h3>
                  <span className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(currentData.buero_premium || 0, priceType)}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Stadtteile/Gewerbegebiete */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            {propertyType === 'wohnen' ? 'Stadtteile' : 'Gewerbegebiete'} - {selectedCity}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {currentStadtteile.map((stadtteil, index) => (
              <div key={index} className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  📍 {stadtteil}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Mietrecht & Anpassungen Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            🏛️ Mietrecht & Anpassungen - {selectedCity}
          </h2>
          
          {propertyType === 'wohnen' ? (
            // Wohnmieten-Sektion
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Kappungsgrenze */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-300 mb-2">
                    Kappungsgrenze
                  </h3>
                  <div className="text-2xl font-bold text-blue-900 dark:text-blue-200">
                    {selectedCity === 'Heidelberg' ? '15%' : '20%'}
                  </div>
                  <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                    {selectedCity === 'Heidelberg' ? 'Milieuschutzgebiet' : 'in 3 Jahren'}
                  </p>
                </div>

                {/* 15-Monats-Regel */}
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-green-800 dark:text-green-300 mb-2">
                    Mindestabstand
                  </h3>
                  <div className="text-2xl font-bold text-green-900 dark:text-green-200">
                    15 Monate
                  </div>
                  <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                    seit letzter Erhöhung
                  </p>
                </div>

                {/* Mietspiegel */}
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-purple-800 dark:text-purple-300 mb-2">
                    Mietspiegel
                  </h3>
                  <div className="text-2xl font-bold text-purple-900 dark:text-purple-200">
                    {selectedCity === 'Mannheim' ? '2025/2026' : selectedCity === 'Heidelberg' ? '2023' : '2024'}
                  </div>
                  <p className="text-sm text-purple-700 dark:text-purple-400 mt-1">
                    Qualifiziert
                  </p>
                </div>
              </div>

              {/* Rechtliche Grundlagen */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="text-md font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  📋 Rechtliche Grundlagen für {selectedCity}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <strong className="text-gray-700 dark:text-gray-300">Erhöhungsgrund:</strong>
                    <p className="text-gray-600 dark:text-gray-400">Ortsübliche Vergleichsmiete (§ 558 BGB)</p>
                  </div>
                  <div>
                    <strong className="text-gray-700 dark:text-gray-300">Kündigungsfrist:</strong>
                    <p className="text-gray-600 dark:text-gray-400">3 Monate Vorlauf erforderlich</p>
                  </div>
                  <div>
                    <strong className="text-gray-700 dark:text-gray-300">Widerspruchsrecht:</strong>
                    <p className="text-gray-600 dark:text-gray-400">Bis Ende des übernächsten Monats</p>
                  </div>
                  <div>
                    <strong className="text-gray-700 dark:text-gray-300">Besonderheiten:</strong>
                    <p className="text-gray-600 dark:text-gray-400">
                      {selectedCity === 'Heidelberg' ? 'Milieuschutz beachten' : 'Mietpreisbremse aktiv'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="text-center space-y-4">
                {/* Mietspiegel Downloads */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-blue-800 dark:text-blue-300 mb-3">
                    📄 Offizielle Mietspiegel & Rechner
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedCity === 'Mannheim' && (
                      <>
                        <a 
                          href="https://www.mannheim.de/sites/default/files/2024-12/Mannheimer_Mietspiegel_2025-2026.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm transition-colors inline-flex items-center gap-2"
                        >
                          <span>📄</span>
                          Mietspiegel 2025/2026 PDF
                        </a>
                        <a 
                          href="https://mietspiegel-berechnen.de/mannheim2025"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm transition-colors inline-flex items-center gap-2"
                        >
                          <span>🧮</span>
                          Online-Rechner
                        </a>
                      </>
                    )}
                    {selectedCity === 'Heidelberg' && (
                      <>
                        <a 
                          href="https://www.heidelberg.de/site/Heidelberg2021/get/documents_E100491582/heidelberg/Objektdatenbank/12/PDF/Statistik/Mietspiegel/12_pdf_Mietspiegel_2023.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm transition-colors inline-flex items-center gap-2"
                        >
                          <span>📄</span>
                          Mietspiegel 2023 PDF
                        </a>
                        <a 
                          href="https://www.heidelberg.de/mietspiegel"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm transition-colors inline-flex items-center gap-2"
                        >
                          <span>🧮</span>
                          Online-Rechner
                        </a>
                      </>
                    )}
                    {selectedCity === 'Ludwigshafen' && (
                      <>
                        <a 
                          href="https://www.bodman-ludwigshafen.de/ceasy/resource/?id=3252&download=1"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm transition-colors inline-flex items-center gap-2"
                        >
                          <span>📄</span>
                          Mietspiegel 2024 PDF
                        </a>
                        <span className="bg-gray-400 text-white px-4 py-2 rounded text-sm inline-flex items-center gap-2">
                          <span>🧮</span>
                          Kein Online-Rechner
                        </span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                    Gültig: {selectedCity === 'Mannheim' ? 'ab 16.12.2024 bis 2026' : selectedCity === 'Heidelberg' ? '2023 (alle 2-3 Jahre aktualisiert)' : '2024'}
                  </p>
                </div>

                <button 
                  onClick={() => window.location.href = '/lease-adjustments?tab=wohnen'}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors inline-flex items-center gap-2"
                >
                  <span>📊</span>
                  Wohnmieten-Erhöhung berechnen
                </button>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Basierend auf {selectedCity}er Mietspiegel und aktueller Rechtslage
                </p>
              </div>
            </div>
          ) : (
            // Gewerbe-Sektion
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Freie Preisgestaltung */}
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-green-800 dark:text-green-300 mb-2">
                    Preisgestaltung
                  </h3>
                  <div className="text-2xl font-bold text-green-900 dark:text-green-200">
                    Frei
                  </div>
                  <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                    Keine Mietpreisbremse
                  </p>
                </div>

                {/* VPI-Indexierung */}
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-orange-800 dark:text-orange-300 mb-2">
                    VPI-Indexierung
                  </h3>
                  <div className="text-2xl font-bold text-orange-900 dark:text-orange-200">
                    +0%
                  </div>
                  <p className="text-sm text-orange-700 dark:text-orange-400 mt-1">
                    Aktueller Index
                  </p>
                </div>

                {/* Staffelmieten */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-300 mb-2">
                    Staffelmieten
                  </h3>
                  <div className="text-2xl font-bold text-blue-900 dark:text-blue-200">
                    Möglich
                  </div>
                  <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                    Vertraglich vereinbar
                  </p>
                </div>
              </div>

              {/* Rechtliche Grundlagen Gewerbe */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="text-md font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  🏢 Gewerbemietrecht für {selectedCity}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <strong className="text-gray-700 dark:text-gray-300">Kündigungsschutz:</strong>
                    <p className="text-gray-600 dark:text-gray-400">Reduziert gegenüber Wohnraum</p>
                  </div>
                  <div>
                    <strong className="text-gray-700 dark:text-gray-300">Mieterhöhungen:</strong>
                    <p className="text-gray-600 dark:text-gray-400">Nach Vereinbarung oder VPI</p>
                  </div>
                  <div>
                    <strong className="text-gray-700 dark:text-gray-300">Indexmieten:</strong>
                    <p className="text-gray-600 dark:text-gray-400">Automatische Anpassung möglich</p>
                  </div>
                  <div>
                    <strong className="text-gray-700 dark:text-gray-300">Nebenkosten:</strong>
                    <p className="text-gray-600 dark:text-gray-400">Meist vollständig umlegbar</p>
                  </div>
                </div>
              </div>

              {/* Action Button Gewerbe */}
              <div className="text-center">
                <button 
                  onClick={() => window.location.href = '/lease-adjustments?tab=gewerbe'}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors inline-flex items-center gap-2"
                >
                  <span>📈</span>
                  Gewerbe-Mietanpassung berechnen
                </button>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  VPI-Indexierung und Staffelmieten für Gewerbeobjekte
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Regional News Section */}
        {regionalNews.length > 0 && (
          <div className="mb-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Regionale Nachrichten & Marktentwicklungen
              </h2>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                🔄 Letzte Aktualisierung: {lastUpdate.toLocaleTimeString('de-DE')}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {regionalNews.map((article, index) => (
                <RegionalNewsCard key={article.id || index} article={article} />
              ))}
            </div>
          </div>
        )}

        {/* Existing Data Sources Section */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Datenquellen</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* BORIS-BW */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">BORIS-BW</h3>
                <span className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded text-xs font-medium">
                  Verfügbar
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Bodenrichtwerte für {propertyType === 'wohnen' ? 'Wohnimmobilien' : 'Gewerbeimmobilien'}
              </p>
              <div className="space-y-2 text-xs text-gray-500 dark:text-gray-400">
                <div>Verfügbare Datensätze: 15.000+</div>
                <div>Kostenlos: Ja</div>
              </div>
              <BorisIntegration />
            </div>

            {/* Open Data BW */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Open Data BW</h3>
                <span className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded text-xs font-medium">
                  Verfügbar
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Infrastrukturdaten für {propertyType === 'wohnen' ? 'Wohnimmobilien' : 'Gewerbeimmobilien'}
              </p>
              <div className="space-y-2 text-xs text-gray-500 dark:text-gray-400">
                <div>Verfügbare Datensätze: 1.608</div>
                <div>Kostenlos: Ja</div>
              </div>
              <a 
                href="https://opendata.baden-wuerttemberg.de" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block mt-4 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm transition-colors"
              >
                Zur Quelle
              </a>
            </div>

            {/* Metropolregion RN */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Metropolregion RN</h3>
                <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded text-xs font-medium">
                  Live-Daten verfügbar
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {propertyType === 'wohnen' ? 'Büromarktbericht 2025' : 'Wirtschaftsdaten Rhein-Neckar'}
              </p>
              <div className="space-y-2 text-xs text-gray-500 dark:text-gray-400">
                <div>Verfügbare Datensätze: 50+</div>
                <div>Kostenlos: Ja</div>
              </div>
              <MetropolregionIntegration />
            </div>

            {/* Standorte Rhein-Neckar */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Standorte Rhein-Neckar</h3>
                <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded text-xs font-medium">
                  Live-Daten verfügbar
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {propertyType === 'wohnen' ? 'Wohnimmobilien Marktdaten' : 'Gewerbeimmobilien Marktdaten'}
              </p>
              <div className="space-y-2 text-xs text-gray-500 dark:text-gray-400">
                <div>Verfügbare Datensätze: 150+</div>
                <div>Kostenlos: Ja</div>
              </div>
              <a 
                href="https://www.standorte-rhein-neckar.de" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block mt-4 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm transition-colors"
              >
                Zur Quelle
              </a>
            </div>

            {/* Gutachterausschuss BW */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Gutachterausschuss BW</h3>
                <span className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded text-xs font-medium">
                  Verfügbar
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Verkehrswerte {propertyType === 'wohnen' ? 'Wohnimmobilien' : 'Gewerbeimmobilien'}
              </p>
              <div className="space-y-2 text-xs text-gray-500 dark:text-gray-400">
                <div>Verfügbare Datensätze: 5.000+</div>
                <div>Kostenlos: Teilweise</div>
              </div>
              <a 
                href="https://www.gutachterausschuss-bw.de" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block mt-4 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm transition-colors"
              >
                Zur Quelle
              </a>
            </div>

            {/* IHK Rhein-Neckar */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">IHK Rhein-Neckar</h3>
                <span className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded text-xs font-medium">
                  Verfügbar
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Wirtschaftsstandort {propertyType === 'wohnen' ? 'Wohnimmobilien' : 'Gewerbeimmobilien'}
              </p>
              <div className="space-y-2 text-xs text-gray-500 dark:text-gray-400">
                <div>Verfügbare Datensätze: 200+</div>
                <div>Kostenlos: Ja</div>
              </div>
              <a 
                href="https://www.ihk.de/rhein-neckar" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block mt-4 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm transition-colors"
              >
                Zur Quelle
              </a>
            </div>

            {/* VRN Verkehrsdaten */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">VRN Verkehrsdaten</h3>
                <span className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded text-xs font-medium">
                  Verfügbar
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Verkehrsanbindung und Mobilität
              </p>
              <div className="space-y-2 text-xs text-gray-500 dark:text-gray-400">
                <div>Verfügbare Datensätze: 100+</div>
                <div>Kostenlos: Ja</div>
              </div>
              <a 
                href="https://www.vrn.de" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block mt-4 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm transition-colors"
              >
                Zur Quelle
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-8">
          <p>
            Datenstand: {lastUpdate.toLocaleDateString('de-DE')} | Regional Insights Portal für Clara360
          </p>
          <p className="mt-1">
            Quellen: BORIS-BW, Open Data BW, Metropolregion Rhein-Neckar, Standorte Rhein-Neckar
          </p>
        </div>
      </div>

      {/* CSS for Perfect News Ticker Animation */}
      <style jsx>{`
        @keyframes marquee-infinite {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        .animate-marquee-infinite {
          animation: marquee-infinite 120s linear infinite;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default RegionaleInsightsPage;

